import Styles from 'common/Prose/Toolbar/MobileWriterFormatBar/MobileWriterFormatBar.module.scss'
import { BoldButton } from './BoldButton'
import { ItalicButton } from './ItalicButton'
import { observer } from 'mobx-react-lite'
import { useAlmanacEditorState } from 'stores/EditorState'
import { useEffect, useState } from 'react'
import { LinkDrawer } from 'common/Prose/Toolbar/MobileWriterFormatBar/LinkDrawer'
import { RevisionPromptInput } from './RevisionPromptInput'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { InsertMenuDrawer } from '../MobileWriterToolbar/InsertMenuDrawer'
import { useDocStore } from 'stores/DocStore'
import { TextStylesMenuDrawer } from '../MobileWriterToolbar/TextStylesMenuDrawer'
import cx from 'classnames'
import { LinkAngled, MessageSquarePlus, Plus, ReceiptLines, Title } from 'blaze-ui/icons'
import { useRootStore } from 'stores/RootStore'
import { useProseInstance } from 'providers/ProseInstanceProvider'
import { CreateCommentDrawer } from './CreateCommentDrawer'
import { Voice } from 'blaze-ui/icons/16'
import { ArrowUpLeftContained } from 'blaze-ui/icons/24'
import { Translate } from 'almanac-ui/icons'
import { AIRequestDrawer } from '../MobileToolbar/AIRequestDrawer/AIRequestDrawer'
import { useImproveQuality } from 'hooks/useImproveQuality'
import { isEmptyDoc } from 'common/Prose/utils/isEmptyDoc'
import { EmptyPromptBarForm } from 'common/Prose/nodeViews/AISuggestion/PromptBar/EmptyPromptBar'
import { isEmptyDocWithTitle } from 'common/Prose/utils/isEmptyDocWithTitle'

export const MobileWriterFormatBar = observer(() => {
  const docStore = useDocStore()
  const drawersStore = useRootStore().drawersStore
  const editorState = useAlmanacEditorState()
  const view = useEditorView()
  const { currentViewRef } = useProseInstance()
  const selection = editorState?.selection.toJSON()
  const [hasSelection, setHasSelection] = useState(false)
  const isDocEmpty = isEmptyDoc(view.state) || isEmptyDocWithTitle(view.state)

  const { improveQuality } = useImproveQuality({ articleId: docStore.articleId, layerId: docStore.layerId || '' })

  const scrollSelectionIntoView = () => {
    view.dispatch(view.state.tr.scrollIntoView())
  }

  useEffect(() => {
    setHasSelection(!editorState?.selection.empty || false)
  }, [selection]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    hasSelection && scrollSelectionIntoView()
  }, [hasSelection]) // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to selection on screen resize, triggered by native keyboard being toggled
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.visualViewport !== 'undefined') {
      window.visualViewport?.addEventListener('scroll', scrollSelectionIntoView)
      window.visualViewport?.addEventListener('resize', scrollSelectionIntoView)

      return () => {
        window.visualViewport?.removeEventListener('resize', scrollSelectionIntoView)
        window.visualViewport?.removeEventListener('scroll', scrollSelectionIntoView)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (isDocEmpty)
    return (
      <div className={Styles.docEmptyWrapper}>
        <EmptyPromptBarForm view={view} articleId={docStore.articleId} layerId={docStore.layerId || ''} />
      </div>
    )

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.suggestedActions}>
        <div
          className={Styles.topButton}
          onClick={() => editorState && drawersStore.openDrawer(AIRequestDrawer, { suggestedAction: 'ChangeLength' })}
        >
          <ReceiptLines />
          <span>Change Length</span>
        </div>
        <div
          className={Styles.topButton}
          onClick={() => editorState && drawersStore.openDrawer(AIRequestDrawer, { suggestedAction: 'EditTone' })}
        >
          <Voice size={20} />
          <span>Edit Tone</span>
        </div>
        <div
          className={Styles.topButton}
          onClick={() => {
            improveQuality()
            if (editorState) drawersStore.openDrawer(AIRequestDrawer, { suggestedAction: 'ImproveQuality' })
          }}
        >
          <ArrowUpLeftContained />
          <span>Improve Quality</span>
        </div>
        <div
          className={Styles.topButton}
          onClick={() => editorState && drawersStore.openDrawer(AIRequestDrawer, { suggestedAction: 'Translate' })}
        >
          <Translate />
          <span>Translate</span>
        </div>
      </div>
      <div
        className={cx(Styles.container, { [Styles.noSelection]: !hasSelection })}
        onScroll={() => {
          setTimeout(() => {
            setTimeout(() => {})
          })
        }}
      >
        <RevisionPromptInput />
        {hasSelection ? (
          <>
            <div
              className={Styles.bottomButton}
              onClick={() => drawersStore.openDrawer(CreateCommentDrawer, { view: currentViewRef.current })}
            >
              <MessageSquarePlus size={24} />
              <span>Comment</span>
            </div>
            <BoldButton />
            <ItalicButton />
            <div
              onClick={() => drawersStore.openDrawer(TextStylesMenuDrawer)}
              className={cx(Styles.bottomButton, { [Styles.disabled]: !docStore.isFocused })}
            >
              <Title />
              <span>Text Styles</span>
            </div>
            <div className={Styles.bottomButton} onClick={() => drawersStore.openDrawer(LinkDrawer)}>
              <LinkAngled />
              <span>Link</span>
            </div>
          </>
        ) : (
          <>
            <div
              className={cx(Styles.bottomButton, { [Styles.disabled]: !docStore.isFocused })}
              onClick={() => drawersStore.openDrawer(InsertMenuDrawer)}
            >
              <Plus />
              <span>Insert</span>
            </div>
            <div className={Styles.separator} />
            <div
              className={cx(Styles.bottomButton, { [Styles.disabled]: !docStore.isFocused })}
              onClick={() => drawersStore.openDrawer(TextStylesMenuDrawer)}
            >
              <Title />
              <span>Text Styles</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
})
